import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi } from "@/hooks/useApi";
import { useToast } from "@/components/feature/ToastProvider";
import {
  KAKAO_JS_KEY_FALLBACK,
  loadKakaoSdk,
  type KakaoNamespace,
} from "@/lib/kakao";
import type { Category, LatLon, MapBounds, MapMarker } from "@/lib/types";
import { useBizmap } from "./hooks/useBizmap";
import BizTopbar from "./components/BizTopbar";
import BizMarkerList from "./components/BizMarkerList";
import BizMapCanvas from "./components/BizMapCanvas";
import BizBottomSheet, { type SheetStage } from "./components/BizBottomSheet";

function matchesSearch(m: MapMarker, query: string): boolean {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  return [m.biz_name, m.name, m.aho, m.address, m.addr, m.biz_addr, m.cate_name, m.cate]
    .some((v) => String(v ?? "").toLowerCase().includes(q));
}

export default function BizmapPage() {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const [cate, setCate] = useState(() => searchParams.get("cate") ?? "");
  const [coords, setCoords] = useState<LatLon | null>(null);
  const [bounds, setBounds] = useState<MapBounds | null>(null);
  const [search, setSearch] = useState("");
  const [selectedNo, setSelectedNo] = useState<number | null>(() => {
    const n = searchParams.get("no");
    return n ? Number(n) : null;
  });
  const [geoState, setGeoState] = useState<"idle" | "loading" | "error">("idle");
  const [sheetStage, setSheetStage] = useState<SheetStage>("half");
  const [kakao, setKakao] = useState<KakaoNamespace | null>(null);
  const [sdkError, setSdkError] = useState<string | null>(null);

  const { data: categories } = useApi<Category[]>("categories.php");
  const { markers, jsKey, center, level, total, loading, error, fetched, reload } = useBizmap({
    cate,
    coords,
    bounds,
  });

  const colorMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    (categories ?? []).forEach((c) => {
      // 업종 필터는 code 기준, 지도 색상은 name/code 둘 다 대응
      map[c.name] = c.color;
      if (c.code) map[c.code] = c.color;
    });
    return map;
  }, [categories]);

  // 카카오맵 SDK 로드 (js_key 우선, 실패 시 대체 키)
  useEffect(() => {
    if (!fetched) return;
    const key = jsKey || KAKAO_JS_KEY_FALLBACK;
    if (!key) return;
    let active = true;
    setSdkError(null);
    loadKakaoSdk(key)
      .then((k) => {
        if (active) setKakao(k);
      })
      .catch((e: Error) => {
        if (active) setSdkError(e.message);
      });
    return () => {
      active = false;
    };
  }, [fetched, jsKey]);

  const visibleMarkers = useMemo(
    () => markers.filter((m) => matchesSearch(m, search)),
    [markers, search]
  );

  // 필터 없이 전체를 보고 있을 때만 마커 전체가 보이도록 범위를 맞춘다.
  const fitBounds = !coords && !bounds && !search;

  const handleCateChange = (next: string) => {
    setCate(next);
    setBounds(null);
    setSelectedNo(null);
  };

  const handleMyLocation = () => {
    if (!navigator.geolocation) {
      setGeoState("error");
      toast("위치 권한이 필요합니다", "error");
      return;
    }
    setGeoState("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setBounds(null);
        setGeoState("idle");
      },
      () => {
        setGeoState("error");
        toast("위치 권한이 필요합니다", "error");
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const handleShowAll = () => {
    setCate("");
    setBounds(null);
    setCoords(null);
    setSearch("");
    setSelectedNo(null);
  };

  return (
    <div className="flex h-[calc(100vh-4rem)] w-full flex-col">
      <BizTopbar
        search={search}
        onSearchChange={setSearch}
        cate={cate}
        onCateChange={handleCateChange}
        categories={categories ?? []}
        geoState={geoState}
        onMyLocation={handleMyLocation}
      />

      <div className="relative min-h-0 flex-1 lg:grid lg:grid-cols-[380px_1fr]">
        {/* 데스크톱 목록 패널 */}
        <aside className="hidden min-h-0 flex-col border-r border-background-200 bg-background-50 lg:flex">
          <BizMarkerList
            markers={visibleMarkers}
            total={total}
            loading={loading}
            error={error}
            selectedNo={selectedNo}
            colorMap={colorMap}
            onSelect={setSelectedNo}
            onRetry={reload}
            onShowAll={handleShowAll}
          />
        </aside>

        {/* 지도 */}
        <div className="absolute inset-0 lg:static lg:h-full">
          <BizMapCanvas
            kakao={kakao}
            markers={visibleMarkers}
            center={center}
            level={level}
            colorMap={colorMap}
            selectedNo={selectedNo}
            myLocation={coords}
            loading={loading}
            sdkError={sdkError}
            fitBounds={fitBounds}
            onSelect={setSelectedNo}
            onResearch={setBounds}
            onMapTouch={() => setSheetStage("collapsed")}
          />
        </div>

        {/* 모바일 바텀시트 */}
        <div className="absolute inset-x-0 bottom-0 z-20 lg:hidden">
          <BizBottomSheet stage={sheetStage} onStageChange={setSheetStage}>
            <BizMarkerList
              markers={visibleMarkers}
              total={total}
              loading={loading}
              error={error}
              selectedNo={selectedNo}
              colorMap={colorMap}
              onSelect={setSelectedNo}
              onRetry={reload}
              onShowAll={handleShowAll}
            />
          </BizBottomSheet>
        </div>
      </div>
    </div>
  );
}